import React, {useEffect, useContext} from 'react';
import * as R from 'ramda';
import BootstrapTable from 'react-bootstrap-table-next';
import {useTranslation} from 'react-i18next';
import querystring from 'querystring';
import paginationFactory, {
  PaginationProvider,
  SizePerPageDropdownStandalone,
  PaginationTotalStandalone,
  PaginationListStandalone,
} from 'react-bootstrap-table2-paginator';
import {Row, Col} from 'reactstrap';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';

import Pop from '@/b2b/components/Pop';
import styles from '@/b2b/index.module.css';

import Icon from '@/views/shared/Icon';
import {SortableLabel} from '@/views/Layout/b2b/SortableList';
import DownloadFileButton from '@/views/shared/DownloadFileButton';

import ServiceRecordDetail from '@/b2b/pages/ServiceRecord/ServiceRecordDetail';

import useAPI from '@/hooks/useAPI';
import {srPolicyList} from '@/b2b/apis';
import {ServiceRecordCtx} from '@/b2b/contexts';
import LoadingOverlay from 'react-loading-overlay';

const ServiceRecordList = () => {
  const [{paging, queryParams}, dispatch] = useContext(ServiceRecordCtx);
  const {result, loading, trigger} = useAPI(srPolicyList, {pending: true});
  const {items = [], totalSize = 0} = R.pathOr({}, ['data'], result);
  const {t: tr} = useTranslation();
  const subtr = tr('sr:table_fields');

  useEffect(
    () => {
      trigger(queryParams);
    },
    [queryParams]
  );

  const columns = [
    {
      dataField: 'PolicyCat',
      text: '',
      formatter: (cell, row) => <SortableLabel shortCode={row.category} />,
      headerStyle: {width: '2%'},
    },
    {
      dataField: 'policyNo',
      text: subtr.policy_no,
      formatter: (cell, row) => (
        <div>
          <span>
            <b>{row.policyNo}</b>
          </span>
          <br />
          <span>{row.productName}</span>
        </div>
      ),
      headerStyle: {width: '18%'},
      sort: true,
    },
    {
      dataField: 'clientName',
      text: subtr.client,
      headerStyle: {width: '12%'},
    },
    {
      dataField: 'insuredName',
      text: subtr.insured,
      headerStyle: {width: '12%'},
    },
    {
      dataField: 'type',
      text: subtr.sr_type,
      headerStyle: {width: '10%'},
    },
    {
      dataField: 'status',
      text: subtr.sr_status,
      headerStyle: {width: '9%'},
      sort: true,
      formatter: (cell, row) => (
        <span>
          <b>{row.status}</b>
        </span>
      ),
    },
    {
      dataField: 'createdDate',
      text: subtr.created_date,
      headerStyle: {width: '10%'},
      sort: true,
    },
    {
      dataField: 'completedDate',
      text: subtr.complete_date,
      headerStyle: {width: '12%'},
      sort: true,
    },
    {
      dataField: 'consultant',
      text: subtr.consultant,
      headerStyle: {width: '10%'},
    },
    {
      dataField: 'followupId',
      text: subtr.details,
      formatter: (cell, row) => (
        <Pop
          provider={{
            component: ServiceRecordDetail,
            followupId: row.followupId,
          }}
          icon='align-justify'
        />
      ),
    },
  ];

  const paginationOpts = {
    custom: true,
    totalSize,
    page: paging.page,
    paginationTotalRenderer: (from, to, size) => (
      <span>{tr('paginate_status', {from, to, size})}</span>
    ),
    sizePerPageList: [10, 20, 30],
  };

  const url = () => `/b2b/sr_export?${querystring.stringify(queryParams)}`;

  const onTableChange = (type, {page, sizePerPage, sortField, sortOrder}) => {
    dispatch({
      type: 'changePaging',
      payload: {page, sizePerPage, sortField, sortOrder},
    });
  };

  return (
    <LoadingOverlay active={loading} spinner text={tr('loading')}>
      <PaginationProvider pagination={paginationFactory(paginationOpts)}>
        {({paginationProps, paginationTableProps}) => (
          <>
            <BootstrapTable
              bootstrap4
              remote
              keyField='uniqueId'
              data={items}
              columns={columns}
              bordered={false}
              onTableChange={onTableChange}
              {...paginationTableProps}
            />
            <Row>
              <Col md='4'>
                <DownloadFileButton
                  className={`btn btn-outline-primary btn-sm ${styles.btn_sm}`}
                  path={url()}
                >
                  <span>
                    {' '}
                    <Icon icon='file-excel' /> {tr('sr:export_excel')}
                  </span>
                </DownloadFileButton>
                &nbsp;
                <PaginationTotalStandalone {...paginationProps} />
              </Col>
              <Col md='4 text-center'>
                <SizePerPageDropdownStandalone
                  {...paginationProps}
                  btnContextual={`btn btn-primary btn-sm ${styles.btn_sm}`}
                />{' '}
                {tr('per_page')}
              </Col>
              <Col md='4 text-right' className={styles.pagination_list}>
                <PaginationListStandalone {...paginationProps} />
              </Col>
            </Row>
          </>
        )}
      </PaginationProvider>
    </LoadingOverlay>
  );
};

export default ServiceRecordList;
